import React, { useEffect, useMemo, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import { FaDownload, FaFilePdf, FaPlus, FaPrint, FaTrash, FaUtensils } from 'react-icons/fa';
import SEO from '../components/SEO';
import { applyMobileInputHints } from '../utils/formEnhancements';

const themes = {
  classic: {
    name: 'Classic Bistro',
    bg: '#f8f1e7',
    panel: '#fffaf3',
    accent: '#a0522d',
    accentSoft: '#f4d3bf',
    text: '#2c1d14',
    muted: '#6b4e3d',
    highlight: '#efb34d',
  },
  modern: {
    name: 'Modern Chic',
    bg: '#f3f4f6',
    panel: '#ffffff',
    accent: '#0f172a',
    accentSoft: '#dbeafe',
    text: '#111827',
    muted: '#475569',
    highlight: '#f97316',
  },
  luxury: {
    name: 'Luxury Gold',
    bg: '#1f1b1a',
    panel: '#2b221f',
    accent: '#d4af37',
    accentSoft: '#3a2a1b',
    text: '#f8f3ea',
    muted: '#e0d3b3',
    highlight: '#d4af37',
  },
  tropical: {
    name: 'Tropical Fresh',
    bg: '#edfdf4',
    panel: '#ffffff',
    accent: '#0f9f6e',
    accentSoft: '#d1fae5',
    text: '#12362b',
    muted: '#42655d',
    highlight: '#facc15',
  },
};

const layouts = {
  editorial: {
    name: 'Editorial',
    headerHeight: 220,
    accentBar: true,
    border: true,
  },
  premium: {
    name: 'Premium Gold',
    headerHeight: 250,
    accentBar: true,
    border: false,
  },
  organic: {
    name: 'Organic Modern',
    headerHeight: 200,
    accentBar: false,
    border: true,
  },
  blacktie: {
    name: 'Black Tie',
    headerHeight: 230,
    accentBar: true,
    border: false,
  },
};

const createItem = (name = 'New Dish', price = 0, description = 'Signature taste') => ({
  id: Date.now() + Math.random(),
  name,
  price,
  description,
});

const createSection = (name = 'Section Name') => ({
  id: Date.now() + Math.random(),
  name,
  items: [createItem()],
});

const initialSections = [
  {
    id: 1,
    name: 'Starters',
    items: [
      createItem('Crispy Corn', 179, 'Corn tossed with garlic and chilli oil'),
      createItem('Paneer Tikka', 289, 'Smoky paneer cubes with mint dip'),
      createItem('Loaded Nachos', 249, 'Corn chips with salsa, cheese and jalapenos'),
    ],
  },
  {
    id: 2,
    name: 'Main Course',
    items: [
      createItem('Butter Chicken', 429, 'Creamy tomato gravy with roasted spices'),
      createItem('Paneer Butter Masala', 379, 'Rich, silky gravy with soft cottage cheese'),
      createItem('Veg Biryani', 329, 'Aromatic basmati rice with vegetables and saffron'),
    ],
  },
  {
    id: 3,
    name: 'Desserts',
    items: [
      createItem('Gulab Jamun', 149, 'Warm syrup-soaked dessert'),
      createItem('Brownie Sundae', 189, 'Chocolate brownie topped with ice cream'),
    ],
  },
];

const formatInr = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

const loadImage = (src) => new Promise((resolve) => {
  if (!src) {
    resolve(null);
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const normalizeImageToPng = (src) => new Promise((resolve) => {
  if (!src) {
    resolve(null);
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;

    const context = canvas.getContext('2d');
    if (!context) {
      resolve(null);
      return;
    }

    context.drawImage(img, 0, 0, canvas.width, canvas.height);
    resolve(canvas.toDataURL('image/png'));
  };
  img.onerror = () => resolve(null);
  img.src = src;
});

const buildTintedBackgroundImage = (src, opacity = 0.28) => new Promise((resolve) => {
  if (!src) {
    resolve(null);
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const canvas = document.createElement('canvas');
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      resolve(null);
      return;
    }

    context.drawImage(img, 0, 0, width, height);
    context.fillStyle = `rgba(255, 255, 255, ${opacity})`;
    context.fillRect(0, 0, width, height);
    resolve(canvas.toDataURL('image/png'));
  };
  img.onerror = () => resolve(null);
  img.src = src;
});

const drawImageCover = (ctx, img, x, y, w, h) => {
  if (!img) return;

  const ratio = img.width / img.height;
  const targetRatio = w / h;
  let drawW = w;
  let drawH = h;
  let offsetX = x;
  let offsetY = y;

  if (ratio > targetRatio) {
    drawH = h;
    drawW = h * ratio;
    offsetX = x + (w - drawW) / 2;
  } else {
    drawW = w;
    drawH = w / ratio;
    offsetY = y + (h - drawH) / 2;
  }

  ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
};

const wrapText = (ctx, text, maxWidth) => {
  if (!text) return [''];
  const words = String(text).split(' ');
  const lines = [];
  let current = '';

  words.forEach((word) => {
    const check = current ? `${current} ${word}` : word;
    if (ctx.measureText(check).width <= maxWidth) {
      current = check;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  });

  if (current) lines.push(current);
  return lines;
};

const RestaurantMenuGenerator = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [selectedLayout, setSelectedLayout] = useState('editorial');
  const [restaurantName, setRestaurantName] = useState('The Spice Table');
  const [tagline, setTagline] = useState('Fresh flavor • warm hospitality');
  const [subtitle, setSubtitle] = useState('Signature menu');
  const [address, setAddress] = useState('MG Road, Bengaluru');
  const [phone, setPhone] = useState('+91 98765 43210');
  const [sections, setSections] = useState(initialSections);
  const [logoData, setLogoData] = useState('');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [isCompactViewport, setIsCompactViewport] = useState(false);
  const pageRef = useRef(null);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsCompactViewport(window.innerWidth <= 768);
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);

    return () => {
      window.removeEventListener('resize', updateViewportMode);
    };
  }, []);

  useEffect(() => {
    applyMobileInputHints(pageRef.current);
  }, [sections.length, phone]);

  const theme = themes[selectedTemplate] || themes.classic;
  const layout = layouts[selectedLayout] || layouts.editorial;

  const totalItems = useMemo(
    () => sections.reduce((sum, section) => sum + section.items.length, 0),
    [sections]
  );

  const updateSection = (sectionId, field, value) => {
    setSections((current) =>
      current.map((section) =>
        section.id === sectionId ? { ...section, [field]: value } : section
      )
    );
  };

  const updateItem = (sectionId, itemId, field, value) => {
    setSections((current) =>
      current.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          items: section.items.map((item) =>
            item.id === itemId ? { ...item, [field]: value } : item
          ),
        };
      })
    );
  };

  const addSection = () => {
    setSections((current) => [...current, createSection(`Section ${current.length + 1}`)]);
  };

  const removeSection = (sectionId) => {
    setSections((current) => {
      if (current.length === 1) return current;
      return current.filter((section) => section.id !== sectionId);
    });
  };

  const addItem = (sectionId) => {
    setSections((current) =>
      current.map((section) =>
        section.id === sectionId
          ? { ...section, items: [...section.items, createItem('New Item', 0, 'Freshly prepared')] }
          : section
      )
    );
  };

  const removeItem = (sectionId, itemId) => {
    setSections((current) =>
      current.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          items: section.items.length > 1 ? section.items.filter((item) => item.id !== itemId) : section.items,
        };
      })
    );
  };

  const handleDownload = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const width = 1080;
    const height = 1400 + Math.max(sections.length * 120, 0);

    canvas.width = width;
    canvas.height = height;

    const background = await loadImage(backgroundImage);
    const logo = await loadImage(logoData);

    if (background) {
      ctx.fillStyle = theme.bg;
      ctx.fillRect(0, 0, width, height);
      drawImageCover(ctx, background, 0, 0, width, height);
      ctx.fillStyle = 'rgba(255,255,255,0.25)';
      ctx.fillRect(0, 0, width, height);
    } else {
      ctx.fillStyle = theme.bg;
      ctx.fillRect(0, 0, width, height);
    }

    const cardX = 60;
    const cardY = 60;
    const cardW = width - 120;
    const headerHeight = layout.headerHeight || 220;

    const headerFill = layout.name === 'Black Tie' || layout.name === 'Premium Gold' ? 'rgba(18,18,18,0.92)' : theme.panel;
    ctx.fillStyle = headerFill;
    ctx.fillRect(cardX, cardY, cardW, headerHeight);

    if (layout.accentBar) {
      ctx.fillStyle = theme.accent;
      ctx.fillRect(cardX, cardY, cardW, layout.name === 'Black Tie' ? 18 : 22);
    }

    if (logo) {
      const logoSize = 76;
      const logoX = cardX + 30;
      const logoY = cardY + 28;
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(logoX, logoY, logoSize + 18, logoSize + 18, 18);
      ctx.clip();
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.fillRect(logoX, logoY, logoSize + 18, logoSize + 18);
      ctx.drawImage(logo, logoX + 9, logoY + 9, logoSize, logoSize);
      ctx.restore();
    }

    ctx.fillStyle = layout.name === 'Black Tie' || layout.name === 'Premium Gold' ? '#f8f3ea' : theme.text;
    ctx.font = '700 42px Arial';
    ctx.fillText(restaurantName, logo ? cardX + 130 : cardX + 30, cardY + 92);
    ctx.font = '500 22px Arial';
    ctx.fillStyle = layout.name === 'Black Tie' || layout.name === 'Premium Gold' ? '#e8d7b3' : theme.muted;
    ctx.fillText(tagline, logo ? cardX + 130 : cardX + 30, cardY + 132);
    ctx.fillStyle = theme.accent;
    ctx.font = '700 18px Arial';
    ctx.fillText(subtitle.toUpperCase(), logo ? cardX + 130 : cardX + 30, cardY + 170);
    ctx.fillStyle = theme.highlight;
    ctx.fillRect(logo ? cardX + 130 : cardX + 30, cardY + 182, 180, 6);

    let yCursor = cardY + headerHeight + 40;

    sections.forEach((section) => {
      ctx.fillStyle = layout.name === 'Black Tie' || layout.name === 'Premium Gold' ? 'rgba(17,17,17,0.74)' : theme.panel;
      ctx.fillRect(cardX, yCursor, cardW, 62);
      ctx.strokeStyle = theme.accentSoft;
      ctx.lineWidth = layout.border ? 2 : 1;
      ctx.strokeRect(cardX, yCursor, cardW, 62);

      ctx.fillStyle = theme.accent;
      ctx.fillRect(cardX, yCursor, 10, 62);
      ctx.fillStyle = layout.name === 'Black Tie' || layout.name === 'Premium Gold' ? '#f8f3ea' : theme.text;
      ctx.font = '700 28px Arial';
      ctx.fillText(section.name, cardX + 28, yCursor + 38);

      yCursor += 82;

      section.items.forEach((item) => {
        const itemBoxY = yCursor;
        ctx.fillStyle = layout.name === 'Black Tie' || layout.name === 'Premium Gold' ? 'rgba(255,255,255,0.02)' : theme.panel;
        ctx.fillRect(cardX, itemBoxY, cardW, 100);

        if (layout.border) {
          ctx.strokeStyle = 'rgba(0,0,0,0.06)';
          ctx.strokeRect(cardX, itemBoxY, cardW, 100);
        }

        ctx.fillStyle = layout.name === 'Black Tie' || layout.name === 'Premium Gold' ? '#f8f3ea' : theme.text;
        ctx.font = '700 26px Arial';
        const titleLines = wrapText(ctx, item.name, cardW - 280);
        ctx.fillText(titleLines[0], cardX + 24, itemBoxY + 36);
        if (titleLines[1]) {
          ctx.fillText(titleLines[1], cardX + 24, itemBoxY + 64);
        }

        ctx.fillStyle = theme.accent;
        ctx.font = '700 24px Arial';
        ctx.fillText(formatInr(item.price), cardX + cardW - 170, itemBoxY + 38);

        if (item.description) {
          ctx.fillStyle = layout.name === 'Black Tie' || layout.name === 'Premium Gold' ? '#cec3ae' : theme.muted;
          ctx.font = '400 18px Arial';
          const descLines = wrapText(ctx, item.description, cardW - 220);
          descLines.slice(0, 2).forEach((line, lineIndex) => {
            ctx.fillText(line, cardX + 24, itemBoxY + 82 + lineIndex * 18);
          });
        }

        yCursor += 112;
      });

      yCursor += 20;
    });

    const footerY = Math.max(height - 170, yCursor + 30);
    ctx.fillStyle = layout.name === 'Black Tie' || layout.name === 'Premium Gold' ? 'rgba(17,17,17,0.82)' : theme.panel;
    ctx.fillRect(cardX, footerY, cardW, 110);
    ctx.fillStyle = theme.accent;
    ctx.font = '700 24px Arial';
    ctx.fillText('Call / Visit', cardX + 28, footerY + 38);
    ctx.fillStyle = layout.name === 'Black Tie' || layout.name === 'Premium Gold' ? '#f8f3ea' : theme.text;
    ctx.font = '500 20px Arial';
    ctx.fillText(address, cardX + 30, footerY + 72);
    ctx.fillText(phone, cardX + 30, footerY + 98);

    const image = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = image;
    link.download = `${restaurantName.toLowerCase().replace(/\s+/g, '-')}-menu.png`;
    link.click();
  };

  const handlePrint = () => {
    window.print();
  };

  const hexToRgb = (hex) => {
    const value = hex.replace('#', '');
    const normalized = value.length === 3 ? value.split('').map((char) => char + char).join('') : value;
    const parsed = Number.parseInt(normalized, 16);

    return {
      r: (parsed >> 16) & 255,
      g: (parsed >> 8) & 255,
      b: parsed & 255,
    };
  };

  const handleDownloadPdf = async () => {
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 36;
    const accent = hexToRgb(theme.accent);
    const text = hexToRgb(theme.text);
    const muted = hexToRgb(theme.muted);
    const panel = hexToRgb(theme.panel);
    const bg = hexToRgb(theme.bg);
    const logo = await normalizeImageToPng(logoData);
    const background = await buildTintedBackgroundImage(backgroundImage, 0.28);

    let y = 34;

    const addPageBreak = (neededHeight) => {
      if (y + neededHeight > pageHeight - 80) {
        pdf.addPage();
        y = 40;
      }
    };

    if (background) {
      pdf.setFillColor(bg.r, bg.g, bg.b);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      pdf.addImage(background, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
    } else {
      pdf.setFillColor(bg.r, bg.g, bg.b);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    }

    pdf.setFillColor(accent.r, accent.g, accent.b);
    pdf.roundedRect(margin, y, pageWidth - margin * 2, 88, 16, 16, 'F');
    y += 24;

    if (logo) {
      pdf.addImage(logo, 'PNG', margin + 18, y + 8, 42, 42, undefined, 'FAST');
    }

    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text(subtitle.toUpperCase(), margin + 18 + (logo ? 60 : 0), y + 12);
    pdf.setFontSize(24);
    pdf.text(restaurantName, margin + 18 + (logo ? 60 : 0), y + 42, { maxWidth: pageWidth - margin * 2 - 120 });
    pdf.setFontSize(10);
    pdf.setTextColor(255, 255, 255);
    pdf.text(tagline, margin + 18 + (logo ? 60 : 0), y + 62);

    y = 140;

    sections.forEach((section) => {
      addPageBreak(120 + section.items.length * 54);

      pdf.setFillColor(panel.r, panel.g, panel.b);
      pdf.roundedRect(margin, y, pageWidth - margin * 2, 28, 10, 10, 'F');
      pdf.setTextColor(text.r, text.g, text.b);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(15);
      pdf.text(section.name, margin + 18, y + 18);
      y += 40;

      section.items.forEach((item) => {
        addPageBreak(48);

        pdf.setTextColor(text.r, text.g, text.b);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        const itemName = item.name.length > 40 ? `${item.name.substring(0, 38)}...` : item.name;
        pdf.text(itemName, margin + 14, y + 12);
        pdf.setTextColor(accent.r, accent.g, accent.b);
        pdf.text(formatInr(item.price), pageWidth - margin - 14, y + 12, { align: 'right' });

        if (item.description) {
          pdf.setTextColor(muted.r, muted.g, muted.b);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          const descLines = pdf.splitTextToSize(item.description, pageWidth - margin * 2 - 110);
          pdf.text(descLines, margin + 14, y + 26);
          y += 18 + descLines.length * 11;
        } else {
          y += 18;
        }

        y += 16;
      });

      y += 12;
    });

    addPageBreak(70);

    pdf.setFillColor(panel.r, panel.g, panel.b);
    pdf.roundedRect(margin, Math.max(y + 8, pageHeight - 120), pageWidth - margin * 2, 70, 12, 12, 'F');
    pdf.setTextColor(text.r, text.g, text.b);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text('Visit us', margin + 18, pageHeight - 92);
    pdf.text('Call', pageWidth - margin - 120, pageHeight - 92);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(muted.r, muted.g, muted.b);
    pdf.text(address, margin + 18, pageHeight - 78);
    pdf.text(phone, pageWidth - margin - 120, pageHeight - 78);

    pdf.save(`${restaurantName.toLowerCase().replace(/\s+/g, '-')}-menu.pdf`);
  };

  return (
    <div className="calculator-page fade-in" style={{ paddingBottom: 32 }} ref={pageRef}>
      <SEO
        title="Restaurant Menu Generator"
        description="Create professional digital restaurant menus with multiple templates, custom sections, and instant PNG download."
        keywords="restaurant menu generator, cafe menu design, menu maker, restaurant menu template, food menu maker"
        canonicalPath="/restaurant-menu-generator"
      />

      <div className="calculator-header">
        <h1>Restaurant Menu Generator</h1>
        <p>Build beautiful restaurant menus with multiple templates and export-ready layouts</p>
      </div>

      <div className="calculator-shell" style={{ display: 'grid', gap: isCompactViewport ? 16 : 24, gridTemplateColumns: isCompactViewport ? '1fr' : 'minmax(280px, 420px) minmax(0, 1fr)' }}>
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'grid', gap: 18 }}>
            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>Template</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {Object.entries(themes).map(([key, value]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedTemplate(key)}
                    style={{
                      background: selectedTemplate === key ? value.accent : '#fff',
                      color: selectedTemplate === key ? '#fff' : value.text,
                      border: `1px solid ${value.accent}`,
                      borderRadius: 999,
                      padding: '8px 14px',
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    {value.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>Print layout</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {Object.entries(layouts).map(([key, value]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedLayout(key)}
                    style={{
                      background: selectedLayout === key ? theme.accent : '#fff',
                      color: selectedLayout === key ? '#fff' : theme.text,
                      border: `1px solid ${theme.accentSoft}`,
                      borderRadius: 999,
                      padding: '8px 14px',
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    {value.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>Logo upload</label>
              <input
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setLogoData(reader.result);
                  reader.readAsDataURL(file);
                }}
                style={{ ...fieldStyle, padding: '8px 12px' }}
              />
              {logoData && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <img src={logoData} alt="Menu logo preview" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 12 }} />
                  <span style={{ fontSize: 12, color: theme.muted }}>Logo added</span>
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>Menu background image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setBackgroundImage(reader.result);
                  reader.readAsDataURL(file);
                }}
                style={{ ...fieldStyle, padding: '8px 12px' }}
              />
              {backgroundImage && (
                <div style={{ marginTop: 10 }}>
                  <img src={backgroundImage} alt="Menu background preview" style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 12, border: `1px solid ${theme.accentSoft}` }} />
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>Restaurant name</label>
              <input value={restaurantName} onChange={(e) => setRestaurantName(e.target.value)} style={fieldStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>Tagline</label>
              <input value={tagline} onChange={(e) => setTagline(e.target.value)} style={fieldStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>Subtitle</label>
              <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} style={fieldStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>Address</label>
              <input value={address} onChange={(e) => setAddress(e.target.value)} style={fieldStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>Phone</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} style={fieldStyle} />
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button type="button" className="primary-btn" onClick={handleDownload} style={{ width: isCompactViewport ? '100%' : 'auto', justifyContent: 'center' }}>
                <FaDownload /> Download PNG
              </button>
              <button type="button" className="secondary-btn" onClick={handleDownloadPdf} style={{ width: isCompactViewport ? '100%' : 'auto', justifyContent: 'center' }}>
                <FaFilePdf /> Download PDF
              </button>
              <button type="button" className="secondary-btn" onClick={handlePrint} style={{ width: isCompactViewport ? '100%' : 'auto', justifyContent: 'center' }}>
                <FaPrint /> Print
              </button>
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>Menu sections</h3>
            <button type="button" className="primary-btn" onClick={addSection}>
              <FaPlus /> Add section
            </button>
          </div>

          <div style={{ display: 'grid', gap: 18 }}>
            {sections.map((section) => (
              <div key={section.id} style={{ border: `1px solid ${theme.accentSoft}`, borderRadius: 18, padding: 16, background: theme.panel }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                  <input
                    value={section.name}
                    onChange={(e) => updateSection(section.id, 'name', e.target.value)}
                    style={{ ...fieldStyle, flex: 1 }}
                  />
                  <button type="button" className="icon-btn danger" onClick={() => removeSection(section.id)} aria-label="Remove section">
                    <FaTrash />
                  </button>
                </div>

                <div style={{ display: 'grid', gap: 10 }}>
                  {section.items.map((item) => (
                    <div key={item.id} style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: 10, background: '#fff' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : 'minmax(0, 1.4fr) 110px auto', gap: 8, alignItems: 'center' }}>
                        <input
                          value={item.name}
                          onChange={(e) => updateItem(section.id, item.id, 'name', e.target.value)}
                          style={fieldStyle}
                        />
                        <input
                          value={item.price}
                          type="number"
                          min="0"
                          onChange={(e) => updateItem(section.id, item.id, 'price', Number(e.target.value))}
                          style={fieldStyle}
                        />
                        <button type="button" className="icon-btn danger" onClick={() => removeItem(section.id, item.id)} aria-label="Remove item">
                          <FaTrash />
                        </button>
                      </div>

                      <textarea
                        value={item.description}
                        onChange={(e) => updateItem(section.id, item.id, 'description', e.target.value)}
                        style={{ ...fieldStyle, minHeight: 54, marginTop: 8 }}
                      />
                    </div>
                  ))}
                </div>

                <button type="button" className="secondary-btn" style={{ marginTop: 12 }} onClick={() => addItem(section.id)}>
                  <FaPlus /> Add item
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 24, padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, letterSpacing: 1.1, textTransform: 'uppercase', color: theme.accent, fontWeight: 800 }}>
              {theme.name}
            </div>
            <h3 style={{ margin: '4px 0 0', fontSize: 26 }}>{restaurantName}</h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, color: theme.muted }}>
            <FaUtensils /> {totalItems} items
          </div>
        </div>

        <div
          style={{
            background: theme.bg,
            color: theme.text,
            borderRadius: 24,
            border: `1px solid ${theme.accentSoft}`,
            padding: 16,
          }}
        >
          <div
            style={{
              background: theme.panel,
              borderRadius: 20,
              padding: 20,
              boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isCompactViewport ? 'flex-start' : 'flex-start', flexDirection: isCompactViewport ? 'column' : 'row', gap: 12 }}>
              <div>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{restaurantName}</div>
                <div style={{ color: theme.muted, marginTop: 4 }}>{tagline}</div>
              </div>
              <div
                style={{
                  background: theme.accentSoft,
                  color: theme.accent,
                  borderRadius: 999,
                  padding: '8px 12px',
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 1.2,
                }}
              >
                {subtitle}
              </div>
            </div>

            <div style={{ display: 'grid', gap: 18, marginTop: 18 }}>
              {sections.map((section) => (
                <div key={section.id}>
                  <div
                    style={{
                      borderBottom: `2px solid ${theme.accent}`,
                      paddingBottom: 8,
                      marginBottom: 10,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{section.name}</div>
                    <div style={{ color: theme.muted, fontSize: 12, fontWeight: 700 }}>0{section.items.length}</div>
                  </div>

                  <div style={{ display: 'grid', gap: 12 }}>
                    {section.items.map((item) => (
                      <div key={item.id} style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : 'minmax(0,1fr) auto', gap: 12, alignItems: 'start' }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 18 }}>{item.name}</div>
                          <div style={{ color: theme.muted, fontSize: 14, marginTop: 2 }}>{item.description}</div>
                        </div>
                        <div style={{ fontWeight: 800, color: theme.accent, textAlign: isCompactViewport ? 'left' : 'right' }}>{formatInr(item.price)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: isCompactViewport ? 'column' : 'row', gap: 12, marginTop: 20, paddingTop: 18, borderTop: `1px solid ${theme.accentSoft}` }}>
              <div>
                <div style={{ fontWeight: 700 }}>Visit us</div>
                <div style={{ color: theme.muted }}>{address}</div>
              </div>
              <div style={{ textAlign: isCompactViewport ? 'left' : 'right' }}>
                <div style={{ fontWeight: 700 }}>Call</div>
                <div style={{ color: theme.muted }}>{phone}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const fieldStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 12,
  border: '1px solid rgba(15, 23, 42, 0.15)',
  fontSize: 14,
  background: '#fff',
  color: '#111827',
};

export default RestaurantMenuGenerator;
